import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase.js";
import { todayStr } from "../lib/format.js";
import { lateInfo, estimateLateCharge, rentalTotal } from "../lib/rental-time.js";

const SELECT =
  "id,equipment_id,customer_id,start_date,start_time,end_date,end_time," +
  "returned_date,returned_time,rental_charge,delivery_fee,damage_waiver,deposit," +
  "late_charge,fuel_charge,damage_charge,other_fee,other_fee_note,total," +
  "manual_price,status,paid,paid_date,invoice_no,notes,source," +
  "equipment(id,name,day_rate,week_rate,month_rate)," +
  // phone and addresses are for the invoice "Bill to" block
  "customers(id,name,business,email,phone,billing_address,home_address)";

export function useRentals() {
  return useQuery({
    queryKey: ["rentals", "list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rentals")
        .select(SELECT)
        .order("start_date", { ascending: false });
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export function useEquipmentForRental() {
  return useQuery({
    queryKey: ["equipment", "for-rental"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("equipment")
        .select("id,name,day_rate,week_rate,month_rate,status")
        .neq("status", "retired")
        .order("name");
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export function useCustomersForRental() {
  return useQuery({
    queryKey: ["customers", "for-rental"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("id,name,business,email")
        .order("name");
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

/**
 * Claim the next invoice number.
 *
 * v10 read invoiceSeq and wrote back seq+1. In one browser that is fine; with
 * two staff, or a website booking landing at the same moment, both read the
 * same number and two rentals get the same invoice.
 *
 * PostgREST cannot express `invoice_seq = invoice_seq + 1`, so this does a
 * compare-and-swap: write the new value only if the row still holds the value
 * we read. A competing claim changes it, the filter matches nothing, and we
 * retry with the fresh number. Same guarantee, no stored procedure.
 */
async function claimInvoiceNumber(attempt = 0) {
  if (attempt >= 5) {
    throw new Error("Could not reserve an invoice number — too many simultaneous saves. Try again.");
  }

  const { data: current, error: readErr } = await supabase
    .from("settings")
    .select("invoice_seq")
    .eq("id", 1)
    .single();
  if (readErr) throw new Error(`Could not read the invoice counter: ${readErr.message}`);

  const seq = Number(current.invoice_seq) || 1000;
  const next = seq + 1;

  const { data: claimed, error: claimErr } = await supabase
    .from("settings")
    .update({ invoice_seq: next })
    .eq("id", 1)
    .eq("invoice_seq", seq) // only if nobody else moved it
    .select("invoice_seq");
  if (claimErr) throw new Error(`Could not reserve an invoice number: ${claimErr.message}`);

  if (!claimed?.length) return claimInvoiceNumber(attempt + 1);

  return `HAL-${new Date().getFullYear()}-${next}`;
}

export function useSaveRental() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, row, existingInvoiceNo }) => {
      // Only claim a number once per rental; editing must not burn another.
      const invoice_no = existingInvoiceNo || (await claimInvoiceNumber());

      const payload = { ...row, invoice_no };
      const { data, error } = id
        ? await supabase.from("rentals").update(payload).eq("id", id).select("id")
        : await supabase.from("rentals").insert([payload]).select("id");

      if (error) {
        if (error.code === "23505") {
          throw new Error("That invoice number is already in use. Save again to take the next one.");
        }
        throw new Error(error.message);
      }
      if (!data?.length) {
        throw new Error("The database accepted the request but changed nothing — usually a security policy.");
      }
      return data[0];
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rentals"] });
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["equipment"] });
    },
  });
}

export function useDeleteRental() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data, error } = await supabase.from("rentals").delete().eq("id", id).select("id");
      if (error) {
        if (error.code === "23503") {
          throw new Error(
            "This rental has condition reports attached. Remove those first, or cancel " +
              "the rental instead of deleting it."
          );
        }
        throw new Error(error.message);
      }
      if (!data?.length) throw new Error("Nothing was deleted — a security policy may have blocked it.");
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rentals"] }),
  });
}

export function useSetStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => {
      const { data, error } = await supabase.from("rentals").update(patch).eq("id", id).select("id");
      if (error) throw new Error(error.message);
      if (!data?.length) throw new Error("Nothing changed — a security policy may have blocked it.");
      return data[0];
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rentals"] }),
  });
}

/**
 * Check in a rental: stamp the return, and if it came back late, fill in the
 * estimated late charge — but only when one has not already been entered by
 * hand, so a negotiated figure is never overwritten.
 */
export function buildCheckIn(rental, now = new Date()) {
  const returned_date = now.toLocaleDateString("en-CA");
  const returned_time = now.toTimeString().slice(0, 5);

  const updated = { ...rental, status: "returned", returned_date, returned_time };
  const li = lateInfo(updated, now);

  let late_charge = Number(rental.late_charge) || 0;
  if (li && !late_charge) {
    late_charge = estimateLateCharge(rental.equipment?.day_rate, li);
  }

  const total = rentalTotal({ ...updated, late_charge });
  return {
    patch: { status: "returned", returned_date, returned_time, late_charge, total },
    lateInfo: li,
  };
}

export function buildTogglePaid(rental) {
  const paid = !rental.paid;
  return { paid, paid_date: paid ? todayStr() : null };
}
